import { Component, Inject, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators, ValidationErrors } from "@angular/forms";
import {
    NmsWizardStepModel,
    AbstractNmsWizardStep,
    NmsWizardStepValidationResponse,
    NmsWizardStepMapData
} from "@nms-angular-toolkit/nms-wizard";
import { MuxDemuxInfoModel, MuxModel } from "../../../mux-demux.model";
import { MuxDemuxService } from "../../../services/mux-demux.service";
import { ANGULARJS_TRANSLATE, DEVICE_SERVICE } from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";
import { TranslationHelperService } from "@nms-ng2/app/shared/services/util/translation-helper.service";
import { CustomValidatorsService } from "../../../../../../shared/services/validators/custom-validators.service";

/*
* Componente responsável por renderizar a aba de informações do Mux/Demux.
*/
@Component({
    selector: "mux-demux-info-tab",
    templateUrl: "./mux-demux-info-tab.component.html",
    styleUrls: ["./mux-demux-info-tab.component.scss"]
})
export class MuxDemuxInfoTabComponent extends AbstractNmsWizardStep implements OnInit {
    model: MuxDemuxInfoModel = new MuxDemuxInfoModel();
    muxForm: FormGroup;
    muxModels: MuxModel[];
    modelImagePath: object;
    EMPTY = "";

    private readonly maxSerialNumberLength: number = 25;
    private readonly maxNameLength: number = 40;
    private readonly alphanumericPattern: RegExp = /^[a-zA-Z0-9]*$/;
    private readonly alphanumericPatternForPaste: RegExp = /[^a-zA-Z0-9]/g;

    private formFieldsErrorsMap = {};

    constructor(@Inject(DEVICE_SERVICE) private readonly deviceService: any,
                @Inject(ANGULARJS_TRANSLATE) private readonly translate: any,
                private readonly customValidatorService: CustomValidatorsService,
                private readonly translationHelper: TranslationHelperService,
                private readonly muxDemuxService: MuxDemuxService,
                private readonly formBuilder: FormBuilder) {
        super();
        this.model = new MuxDemuxInfoModel();
        this.initForm();
        this.getMuxModels();
        this.initErrorsMap();
    }

    ngOnInit(): void {
        this.muxForm.patchValue({
            name: this.model.name || "",
            serialNumber: this.model.serialNumber || "",
            model: this.model.model || ""
        });
    }

    private initErrorsMap(): void {
        this.formFieldsErrorsMap = {
            name: {
                required: this.translate.instant("add.mux.demux.modal.error.name.must.be.filled"),
                maxlength: this.translate.instant("add.mux.demux.modal.error.name.max.length")
            },
            serialNumber: {
                maxlength: this.translate.instant("add.mux.demux.modal.error.serial.number.max.length"),
                pattern: this.translate.instant("add.mux.demux.modal.error.serial.number.alphanumeric")
            },
            model: {
                required: this.translate.instant("add.mux.demux.modal.error.model.must.be.selected")
            }
        };
    }

    private initForm(): void {
        this.muxForm = this.formBuilder.group({
            name: ["", [Validators.required, Validators.maxLength(this.maxNameLength),
                    this.customValidatorService.notBlankValidator]],
            serialNumber: ["", [Validators.maxLength(this.maxSerialNumberLength), Validators.pattern(this.alphanumericPattern)]],
            model: ["", [Validators.required]]
        });
    }

    async comingFromPreviousStep(muxinfoModel: MuxDemuxInfoModel,
                                wizardStepsCacheMap: NmsWizardStepMapData ): Promise<NmsWizardStepModel> {
        const muxInfoModel = wizardStepsCacheMap["MuxDemuxInfoModel"] as MuxDemuxInfoModel;
        if (muxInfoModel) {
            this.fillFormToEdit(muxInfoModel);
        } else {
            wizardStepsCacheMap["MuxDemuxInfoModel"] = muxinfoModel;
        }
        return Promise.resolve(this.model);
    }

    goingToNextStep(): Promise<NmsWizardStepModel> {
        this.model.name = this.muxForm.get("name")?.value || "";
        this.model.serialNumber = this.muxForm.get("serialNumber")?.value || "";
        this.model.model = this.muxForm.get("model")?.value || "";

        return Promise.resolve(this.model);
    }

    allowPrevious(): Promise<NmsWizardStepValidationResponse> {
        return Promise.resolve(NmsWizardStepValidationResponse.getDefaultValidationResponse());
    }

    async allowNext(): Promise<NmsWizardStepValidationResponse> {
        if (!this.validadeFields()) {
            return Promise.reject();
        }

        return Promise.resolve({ isValid: true });
    }

    getMuxModels(): void {
        this.muxDemuxService.getMuxModels().subscribe((muxModels) => {
            this.muxModels = muxModels;
            this.changeMuxModel();
        });
    }

    changeMuxModel(): void {
        const selectedModelName = this.muxForm.get("model")?.value;

        if (selectedModelName) {
            const selectedModel = this.muxModels.find((muxModel) => muxModel.name === selectedModelName);
            if (selectedModel) {
                this.model.model = selectedModelName;
                this.model.channelAmount = selectedModel.channelAmount;
                this.deviceService.getDeviceImageByModel(selectedModelName).then((image: string) => {
                    this.modelImagePath = {
                        "background-image": `url("data:image/svg+xml;utf8,${encodeURIComponent(image)}")`
                    };
                });
            }
        }
    }

    validadeFields(): boolean {
        this.muxForm.markAllAsTouched();
        return this.muxForm.valid;
    }

    allowOnlyAlphanumeric(event: KeyboardEvent) {
        const char = event.key;

        if (!this.alphanumericPattern.test(char)) {
            event.preventDefault();
        }
    }

    allowOnlyAlphanumericPaste(event: ClipboardEvent) {
        event.preventDefault();
        const pastedText = event.clipboardData?.getData("text") || "";
        const filteredText = pastedText.replace(this.alphanumericPatternForPaste, "");
        const target = event.target as HTMLInputElement;

        if (target) {
            const currentValue = target.value;
            const selectionStart = target.selectionStart || 0;
            const selectionEnd = target.selectionEnd || 0;
            const newValue = currentValue.substring(0, selectionStart) + filteredText + currentValue.substring(selectionEnd);

            this.muxForm.get("serialNumber")?.setValue(newValue);

            const newCursorPosition = selectionStart + filteredText.length;
            target.setSelectionRange(newCursorPosition, newCursorPosition);
        }
    }

    isFieldInvalid(fieldName: string): boolean {
        const control = this.muxForm.get(fieldName);

        return control?.invalid && (control.dirty || control.touched);
    }

    getFieldErrorMessage(fieldName: string): string {
        const control = this.muxForm.get(fieldName);

        if (!control || control.valid || !control.errors || (!control.dirty && !control.touched)) {
            return this.EMPTY;
        }

        const controlErrors: ValidationErrors = control.errors;
        const firstErrorKey = Object.keys(controlErrors)[0];
        const errorMessage = this.formFieldsErrorsMap[fieldName]?.[firstErrorKey];

        return errorMessage || this.getGenericErrorMessage(firstErrorKey, controlErrors[firstErrorKey]);
    }

    private getGenericErrorMessage(errorKey: string, filledName: string): string {
        let errorMessage;

        switch (errorKey) {
            case "required":
                errorMessage = "input.validator.error.required";
                break;
            case "maxlength":
                errorMessage = "input.validator.error.maxlength";
                break;
            case "pattern":
                errorMessage = "input.validator.error.pattern";
                break;
            default:
                errorMessage = "input.validator.error.pattern";
                break;
        }

        return this.translationHelper.translateWithReplacement(errorMessage, filledName);
    }

    private fillFormToEdit(muxDemuxInfoModel: MuxDemuxInfoModel) {
        this.muxForm.patchValue({
            name: muxDemuxInfoModel.name,
            serialNumber: muxDemuxInfoModel.serialNumber,
            model: muxDemuxInfoModel.model
        });
        this.model.resourceId = muxDemuxInfoModel.resourceId;
    }
}
